import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { LphSiteConfig } from './site-config';

export type AppSection = 'admin' | 'sales' | 'directory';

@customElement('lph-nav')
export class LphNav extends LitElement {
  @property({ attribute: false }) config!: LphSiteConfig;
  @property() email = '';
  @property() role = '';
  @property() phone = '';
  @property() section: AppSection = 'sales';
  @state() private menu: 'nav' | 'profile' | '' = '';

  static styles = css`
    :host { display: contents; }
    .brand { display: flex; align-items: center; gap: 0.75rem; min-width: 0; }
    .brand-text { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
    .product-row { display: flex; align-items: center; gap: 0.45rem; min-width: 0; }
    .badge {
      display: inline-flex; align-items: center; justify-content: center;
      padding: 0.12rem 0.45rem; border-radius: 4px; font-size: 0.65rem;
      font-weight: 700; letter-spacing: 0.06em; line-height: 1.2;
      background: #5b9fd4; color: #0f1419; flex-shrink: 0;
    }
    .product-name {
      font-size: clamp(0.8rem, 2.4vw, 0.95rem); font-weight: 600;
      letter-spacing: -0.02em; color: #e8edf2;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .domain { font-size: 0.7rem; color: #6d8299; letter-spacing: 0.01em; white-space: nowrap; }
    .domain span { color: #8fa3b8; }
    .actions {
      display: flex; align-items: center; gap: 0.65rem;
      min-width: 0; margin-left: auto;
    }
    .menu-wrap { position: relative; flex-shrink: 0; }
    .menu-btn {
      display: inline-flex; align-items: center; justify-content: center;
      width: 40px; height: 40px; padding: 0; border: 1px solid #2a3a4a;
      border-radius: 8px; background: #0f1419; color: #e8edf2; cursor: pointer;
    }
    .menu-btn:hover, .menu-btn[aria-expanded='true'],
    .profile-btn:hover, .profile-btn[aria-expanded='true'] { border-color: #5b9fd4; background: #16202a; }
    .menu-btn:focus-visible, .profile-btn:focus-visible { outline: 2px solid #5b9fd4; outline-offset: 2px; }
    .profile-btn {
      display: inline-flex; align-items: center; max-width: min(42vw, 220px);
      height: 40px; padding: 0 0.75rem; border: 1px solid #2a3a4a;
      border-radius: 8px; background: #0f1419; color: #e8edf2; cursor: pointer;
      font: inherit; font-size: 0.84rem;
    }
    .profile-btn span {
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .profile {
      display: grid; gap: 0.55rem; padding: 0.7rem 0.8rem 0.85rem;
    }
    .profile h2 { margin: 0; font-size: 0.95rem; font-weight: 600; }
    .profile dl { display: grid; gap: 0.45rem; margin: 0; }
    .profile dt {
      font-size: 0.65rem; font-weight: 700; letter-spacing: 0.06em;
      text-transform: uppercase; color: #8fa3b8;
    }
    .profile dd { margin: 0.1rem 0 0; font-size: 0.9rem; word-break: break-word; }
    .burger { display: flex; flex-direction: column; justify-content: center; gap: 4px; width: 16px; height: 16px; }
    .burger span {
      display: block; height: 1.5px; width: 100%; background: currentColor; border-radius: 1px;
      transform-origin: center; transition: transform 0.16s ease, opacity 0.16s ease;
    }
    .burger.open span:nth-child(1) { transform: translateY(5.5px) rotate(45deg); }
    .burger.open span:nth-child(2) { opacity: 0; }
    .burger.open span:nth-child(3) { transform: translateY(-5.5px) rotate(-45deg); }
    .menu {
      position: absolute; top: calc(100% + 0.45rem); right: 0; min-width: 260px;
      padding: 0.4rem; border: 1px solid #2a3a4a; border-radius: 10px;
      background: #121a22; box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45); z-index: 50;
    }
    .menu a, .menu button.item {
      display: flex; flex-direction: column; align-items: flex-start; gap: 0.15rem;
      width: 100%; padding: 0.7rem 0.8rem; border: 0; border-radius: 7px;
      background: transparent; color: #e8edf2; text-decoration: none; text-align: left;
      font: inherit; cursor: pointer;
    }
    .menu a:hover, .menu button.item:hover, .menu button.item.current { background: #1a2530; }
    .item-code { font-size: 0.65rem; font-weight: 700; letter-spacing: 0.06em; color: #5b9fd4; }
    .item-label { font-size: 0.9rem; font-weight: 500; }
    .divider { height: 1px; margin: 0.35rem 0.4rem; background: #1e2a36; }
    .danger .item-label { color: #e8a0a0; }
    @media (max-width: 480px) {
      .product-name, .user { display: none; }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
  }

  private _onDocClick = (e: MouseEvent) => {
    if (!this.menu) return;
    if (!e.composedPath().includes(this.renderRoot as EventTarget)) this.menu = '';
  };

  private _onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.menu) this.menu = '';
  };

  private signOut(event: Event) {
    event.preventDefault();
    this.menu = '';
    this.dispatchEvent(new CustomEvent('sign-out', { bubbles: true, composed: true }));
  }

  private toggle(which: 'nav' | 'profile', event: Event) {
    event.stopPropagation();
    this.menu = this.menu === which ? '' : which;
  }

  private choose(section: AppSection) {
    this.menu = '';
    this.dispatchEvent(new CustomEvent('navigate', { detail: section, bubbles: true, composed: true }));
  }

  private roleLabel() {
    if (this.role === 'admin') return 'Admin';
    if (this.role === 'user') return 'User';
    return 'Signed in';
  }

  private phoneLabel() {
    const digits = this.phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    return this.phone || 'Not on file';
  }

  render() {
    if (!this.config) return nothing;
    return html`
      <div class="brand">
        <div class="brand-text">
          <div class="product-row">
            <span class="badge">${this.config.productCode}</span>
            <span class="product-name">${this.config.productName}</span>
          </div>
          <div class="domain">${this.config.domainHtml}.<span>n321sd</span></div>
        </div>
      </div>
      <div class="actions">
        ${this.email
          ? html`
        <div class="menu-wrap">
          <button
            type="button"
            class="profile-btn"
            aria-label="Your profile"
            aria-expanded=${this.menu === 'profile' ? 'true' : 'false'}
            aria-haspopup="dialog"
            @click=${(event: Event) => this.toggle('profile', event)}
          >
            <span>${this.email}</span>
          </button>
          ${this.menu === 'profile'
            ? html`
                <div class="menu profile" role="dialog" aria-label="Your profile">
                  <h2>Profile</h2>
                  <dl>
                    <div>
                      <dt>Email</dt>
                      <dd>${this.email}</dd>
                    </div>
                    <div>
                      <dt>Access</dt>
                      <dd>${this.roleLabel()}</dd>
                    </div>
                    <div>
                      <dt>Mobile</dt>
                      <dd>${this.phoneLabel()}</dd>
                    </div>
                  </dl>
                </div>
              `
            : nothing}
        </div>
        <div class="menu-wrap">
          <button
            type="button"
            class="menu-btn"
            aria-label=${this.menu === 'nav' ? 'Close menu' : 'Open menu'}
            aria-expanded=${this.menu === 'nav' ? 'true' : 'false'}
            aria-haspopup="menu"
            @click=${(event: Event) => this.toggle('nav', event)}
          >
            <span class="burger ${this.menu === 'nav' ? 'open' : ''}" aria-hidden="true"><span></span><span></span><span></span></span>
          </button>
          ${this.menu === 'nav'
            ? html`
                <div class="menu" role="menu">
                  <button type="button" role="menuitem" class="item ${this.section === 'sales' ? 'current' : ''}" @click=${() => this.choose('sales')}>
                    <span class="item-code">FP3</span>
                    <span class="item-label">Prospects</span>
                  </button>
                  <button type="button" role="menuitem" class="item ${this.section === 'directory' ? 'current' : ''}" @click=${() => this.choose('directory')}>
                    <span class="item-code">FAA</span>
                    <span class="item-label">FAA directory</span>
                  </button>
                  <div class="divider"></div>
                  <button type="button" role="menuitem" class="item danger" @click=${this.signOut}>
                    <span class="item-code">ACCOUNT</span>
                    <span class="item-label">Log out</span>
                  </button>
                </div>
              `
            : nothing}
        </div>
      `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lph-nav': LphNav;
  }
}
